// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const adminDoc = await getDoc(doc(db, 'admins', firebaseUser.uid));
        if (adminDoc.exists() && adminDoc.data().role === 'Admin') {
          setUser(firebaseUser);
          setRole('Admin');
        } else {
          await signOut(auth);
          setUser(null);
          setRole(null);
          setAuthError(
            adminDoc.exists()
              ? 'Access denied. You are not an Admin.'
              : 'Access denied. Your account is not registered as Admin.'
          );
        }
      } else {
        setUser(null);
        setRole(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const loginWithEmail = async (email, password) => {
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // ✅ onAuthStateChanged এ admin check শেষ হওয়ার পরে user set হবে
      // navigate Login.js এ user state দেখে করবে, এখানে নয়
    } catch (err) {
      setAuthError(getErrorMessage(err.code));
      throw err;
    }
  };

  const loginWithGoogle = async () => {
    setAuthError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      setAuthError(getErrorMessage(err.code));
      throw err;
    }
  };

  const logout = () => {
    setAuthError(null);
    return signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, role, loading, authError, setAuthError, loginWithEmail, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

function getErrorMessage(code) {
  const map = {
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/too-many-requests': 'Too many attempts. Try again later.',
    'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
    'auth/invalid-credential': 'Invalid credentials. Check email and password.',
  };
  return map[code] || 'Authentication failed. Please try again.';
}